import { supabase } from "@/integrations/supabase/client";
import { UserRole } from "@/contexts/RoleContext";

export interface ToolSetting {
  id: string;
  tool_id: string;
  is_enabled: boolean;
  allowed_roles: string[];
  disabled_message: string | null;
  background_image_url: string | null;
  updated_at: string;
  updated_by: string | null;
}

// Optimized columns
const TOOL_SETTING_COLUMNS = `id, tool_id, is_enabled, allowed_roles, disabled_message, background_image_url, updated_at, updated_by`;

export async function fetchToolSettings(): Promise<ToolSetting[]> {
  const { data, error } = await supabase
    .from('tool_settings')
    .select(TOOL_SETTING_COLUMNS)
    .order('tool_id');

  if (error) {
    console.error('Error fetching tool settings:', error);
    throw error;
  }

  return data || [];
}

export async function updateToolSetting(
  toolId: string,
  updates: Partial<Pick<ToolSetting, 'is_enabled' | 'allowed_roles' | 'disabled_message' | 'background_image_url'>>
): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  
  const { error } = await supabase
    .from('tool_settings')
    .update({
      ...updates,
      updated_by: user?.id
    })
    .eq('tool_id', toolId);

  if (error) {
    console.error('Error updating tool setting:', error);
    throw error;
  }
}

export async function createToolSetting(
  toolId: string,
  settings: Partial<Pick<ToolSetting, 'is_enabled' | 'allowed_roles' | 'disabled_message'>>
): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  
  const { error } = await supabase
    .from('tool_settings')
    .insert({
      tool_id: toolId,
      is_enabled: settings.is_enabled ?? true,
      allowed_roles: settings.allowed_roles ?? ['admin', 'staff', 'partner', 'student'],
      disabled_message: settings.disabled_message ?? null,
      updated_by: user?.id
    });

  if (error) {
    console.error('Error creating tool setting:', error);
    throw error;
  }
}

/**
 * Get effective tool status combining static config and database settings
 * CRITICAL: Always validates against staticAllowedRoles when no DB config exists
 */
export function getEffectiveToolStatus(
  toolId: string,
  userRole: UserRole,
  dbSettings: ToolSetting | undefined,
  staticStatus: 'active' | 'coming_soon' | 'locked',
  staticAllowedRoles: readonly UserRole[] = [] // NEW: Required for proper fallback
): { status: 'active' | 'coming_soon' | 'locked' | 'disabled'; message?: string } {
  
  // If no database setting, use static config WITH role validation
  if (!dbSettings) {
    // CRITICAL FIX: Always validate against static allowed roles
    if (staticAllowedRoles.length > 0 && !staticAllowedRoles.includes(userRole)) {
      return { 
        status: 'locked',
        message: 'Você não tem permissão para acessar esta ferramenta.'
      };
    }
    return { status: staticStatus };
  }

  // If tool is disabled in database
  if (!dbSettings.is_enabled) {
    return { 
      status: 'disabled', 
      message: dbSettings.disabled_message || 'Esta ferramenta está temporariamente indisponível.' 
    };
  }

  // If user role is not in allowed roles from database
  if (!dbSettings.allowed_roles.includes(userRole)) {
    return { 
      status: 'locked',
      message: 'Você não tem permissão para acessar esta ferramenta.'
    };
  }

  // If static config says coming_soon, respect that
  if (staticStatus === 'coming_soon') {
    return { status: 'coming_soon' };
  }

  return { status: 'active' };
}
